import { useRef, useEffect, useState, useCallback } from 'react';

const W = 360;
const H = 420;
const GRAVITY = 0.5;
const FLAP = -7.5;
const PIPE_W = 54;
const GAP = 130;
const PIPE_SPEED = 2.2;
const SPAWN = 95; // frames between pipes

export default function FlappyGame({ onScore }) {
  const canvasRef = useRef();
  const stateRef = useRef();
  const rafRef = useRef();
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | playing | over

  const flap = useCallback(() => {
    const s = stateRef.current;
    if (s && status === 'playing') s.vel = FLAP;
  }, [status]);

  const loop = useCallback(() => {
    const s = stateRef.current;
    const ctx = canvasRef.current.getContext('2d');

    // physics
    s.vel += GRAVITY;
    s.y += s.vel;
    s.frame += 1;

    if (s.frame % SPAWN === 0) {
      const top = 40 + Math.random() * (H - GAP - 120);
      s.pipes.push({ x: W, top, passed: false });
    }
    s.pipes.forEach((p) => { p.x -= PIPE_SPEED; });
    s.pipes = s.pipes.filter((p) => p.x + PIPE_W > 0);

    // scoring
    s.pipes.forEach((p) => {
      if (!p.passed && p.x + PIPE_W < s.x) {
        p.passed = true;
        s.score += 1;
        setScore(s.score);
      }
    });

    // collisions
    const hitGround = s.y + 12 > H || s.y - 12 < 0;
    const hitPipe = s.pipes.some(
      (p) => s.x + 12 > p.x && s.x - 12 < p.x + PIPE_W && (s.y - 12 < p.top || s.y + 12 > p.top + GAP)
    );
    if (hitGround || hitPipe) {
      cancelAnimationFrame(rafRef.current);
      setStatus('over');
      onScore(s.score);
      return;
    }

    // draw
    ctx.fillStyle = '#0d1b2e';
    ctx.fillRect(0, 0, W, H);
    // pipes
    ctx.fillStyle = '#2e7d32';
    s.pipes.forEach((p) => {
      ctx.fillRect(p.x, 0, PIPE_W, p.top);
      ctx.fillRect(p.x, p.top + GAP, PIPE_W, H - p.top - GAP);
    });
    // bird
    ctx.font = '26px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.save();
    ctx.translate(s.x, s.y);
    ctx.rotate(Math.max(-0.4, Math.min(1, s.vel / 12)));
    ctx.fillText('🐤', 0, 0);
    ctx.restore();

    rafRef.current = requestAnimationFrame(loop);
  }, [onScore]);

  const start = useCallback(() => {
    stateRef.current = { x: 90, y: H / 2, vel: 0, pipes: [], frame: 0, score: 0 };
    setScore(0);
    setStatus('playing');
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  useEffect(() => {
    function onKey(e) {
      if (e.code === 'Space') { e.preventDefault(); flap(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flap]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center justify-between w-full">
        <span className="text-gold-500 font-black text-lg">🐤 {score}</span>
        <span className="text-gray-500 text-xs">Space / click to flap</span>
      </div>
      <div className="relative" style={{ width: W, height: H }}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          onClick={flap}
          className="rounded-lg border cursor-pointer"
          style={{ borderColor: '#1e3a5f' }}
        />
        {status !== 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-lg" style={{ backgroundColor: 'rgba(13,27,46,0.85)' }}>
            {status === 'over' && <p className="text-red-400 font-black text-xl">SPLAT 💥</p>}
            {status === 'over' && <p className="text-gray-300 text-sm">{score} pipe{score === 1 ? '' : 's'}. Gravity: undefeated.</p>}
            <button
              onClick={start}
              className="px-5 py-2 rounded font-bold uppercase tracking-widest text-sm"
              style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
            >
              {status === 'idle' ? '▶ Play Flappy' : '↻ Again'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
